import React from "react";
import { useDispatch } from "react-redux";
import {
  Typography,
  useTheme,
  Box,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import { IconBrain } from "../icons/BrainIcon";
import TallyDialog from "../dialog/TallyDialog";
import axios from "../../utils/axios.tsx";
import { conversationActions } from "../../store/conversation.tsx";
import { i18n } from "@lingui/core";
import { Trans } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";
import ModelTrainingIcon from "@mui/icons-material/ModelTraining";

const ConversationDrawerActions = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [isDialogOpen, setIsDialogOpen] = React.useState<boolean>(false);
  const [selectedOption, setSelectedOption] =
    React.useState<string>("OpenAI;gpt-4o");

  const handleOpenDialog = () => {
    setIsDialogOpen(true);
  };

  // Close the dialog
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleOptionChange = (event: SelectChangeEvent<string>) => {
    const model = event.target.value as string;
    setSelectedOption(model);
    dispatch(conversationActions.setSelectedModel(model));
  };

  const handleCreateMemory = async (memoryText: string) => {
    await axios
      .post("/v1/memory/", JSON.stringify({ content: memoryText }))
      .then((response) => {
        dispatch(conversationActions.appendCustomActionResponse(response.data));
      });
    handleCloseDialog();
  };

  return (
    <Box
      marginBottom={2}
      sx={{
        py: "4px",
        paddingLeft: "20px",
        paddingRight: "4px",
      }}
    >
      <Typography
        variant="h4"
        onClick={handleOpenDialog}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: theme.spacing(1),
          cursor: "pointer",
          whiteSpace: "nowrap",
          fontSize: "1rem !important",
          width: "100%",
          lineHeight: "1",
          margin: "0",
          py: "6px",
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
            borderRadius: "6px",
          },
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "20px",
            height: "20px",
            marginLeft: "2px",
          }}
        >
          <IconBrain width="20px" height="20px" fontWeight={"light"} />
        </Box>
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "left",
          }}
        >
          <Trans context={"Span next to a button serving as a button title"}>
            Create a memory
          </Trans>
        </Box>
      </Typography>

      <TallyDialog
        open={isDialogOpen}
        title={i18n._(
          msg({
            context: "Button title enabling the user to create a memory",
            message: "Create a memory",
          }),
        )}
        subtitle={i18n._(
          msg({
            context:
              "Header with explanation what creating a memory does in the application.",
            message:
              "By creating a memory, I will learn the information so I can use it going forward in our conversation",
          }),
        )}
        submitTitle={i18n._(
          msg({
            context: "Button title for creation of a memory",
            message: "Create",
          }),
        )}
        onSubmit={handleCreateMemory}
        initialInputValue=""
        handleDialogClose={handleCloseDialog}
      />

      <Typography
        variant="body2"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: theme.spacing(1),
          marginTop: theme.spacing(1),
          justifyContent: "flex-start",
          fontSize: "1rem !important",
        }}
      >
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "20px",
            height: "20px",
          }}
        >
          <ModelTrainingIcon />
        </Box>
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          <Trans>Choose a model</Trans>
        </Box>
      </Typography>

      <Select
        value={selectedOption}
        onChange={handleOptionChange}
        displayEmpty
        sx={{
          marginTop: theme.spacing(0.5),
          width: "100%",
          border: "none",
          boxShadow: "none",
          "&:focus": {
            border: "none",
            boxShadow: "none",
          },
        }}
      >
        <MenuItem value="OpenAI;gpt-4o">OpenAI</MenuItem>
        <MenuItem value="Nebius;deepseek-ai/DeepSeek-V3-0324-fast">
          DeepSeek-V3
        </MenuItem>
        <MenuItem value="Nebius;deepseek-ai/DeepSeek-R1">DeepSeek-R1</MenuItem>
        <MenuItem value="Nebius;meta-llama/Meta-Llama-3.1-70B-Instruct-fast">
          Meta-Llama
        </MenuItem>
      </Select>
    </Box>
  );
};

export default ConversationDrawerActions;
